import { Link, useRouter } from "@shared/client/navigation";
import { useState } from "react";
import { LoaderCircle, Plus, Search } from "lucide-react";
import type { ClientListItem } from "@shared/lib/admin-db";
import { formatCents, formatDate } from "@shared/lib/format";
import { AdminChip, AdminEmpty, AdminMeta, AdminPanel } from "@shared/components/blocks";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared/components/ui/table";

export const CLIENT_STATUS_LABELS: Record<string, string> = {
  lead: "Prospect",
  trial: "Essai",
  active: "Actif",
  churned: "Perdu",
};

const STATUS_FILTERS = [
  { value: "all", label: "Tous les statuts" },
  { value: "lead", label: "Prospects" },
  { value: "trial", label: "En essai" },
  { value: "active", label: "Actifs" },
  { value: "churned", label: "Perdus" },
];

export function ClientsView({
  clients,
  search,
  status,
}: {
  clients: ClientListItem[];
  search: string;
  status: string;
}) {
  const router = useRouter();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function applyFilters(nextSearch: string, nextStatus: string) {
    const params = new URLSearchParams();
    if (nextSearch) params.set("search", nextSearch);
    if (nextStatus && nextStatus !== "all") params.set("status", nextStatus);
    router.replace(`/admin/clients${params.size ? `?${params}` : ""}`);
  }

  async function createClient(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const response = await fetch("/api/admin/clients", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, website: website || undefined, status: "lead" }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Création impossible.");
      setName("");
      setWebsite("");
      setCreating(false);
      router.push(`/admin/clients/${data.client.id}`);
      router.refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Création impossible.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <AdminMeta
        note={
          <Button onClick={() => setCreating((value) => !value)} size="sm" type="button" variant="outline">
            <Plus /> Nouveau client
          </Button>
        }
      >
        {clients.length} client{clients.length > 1 ? "s" : ""}
      </AdminMeta>

      <AdminPanel flush>
        <div className="flex flex-wrap items-center gap-3 border-b px-4 py-3">
          <form
            className="relative min-w-[220px] flex-1"
            onSubmit={(event) => {
              event.preventDefault();
              applyFilters(new FormData(event.currentTarget).get("search")?.toString() || "", status);
            }}
          >
            <Search aria-hidden="true" className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="h-9 pl-9" defaultValue={search} name="search" placeholder="Rechercher un client…" />
          </form>
          <Select onValueChange={(value) => applyFilters(search, value)} value={status}>
            <SelectTrigger aria-label="Filtrer par statut" className="h-9 text-xs font-semibold">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((filter) => (
                <SelectItem key={filter.value} value={filter.value}>{filter.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {creating && (
          <form className="flex flex-wrap items-center gap-3 border-b bg-muted/40 px-4 py-3.5" onSubmit={createClient}>
            <Input
              className="h-9 max-w-[280px] bg-background"
              onChange={(event) => setName(event.target.value)}
              placeholder="Nom de l'entreprise"
              required
              value={name}
            />
            <Input
              className="h-9 max-w-[280px] bg-background"
              inputMode="url"
              onChange={(event) => setWebsite(event.target.value)}
              placeholder="Site web (optionnel)"
              value={website}
            />
            <Button disabled={busy} size="sm" type="submit">
              {busy ? <LoaderCircle className="animate-spin" /> : <Plus />} Créer
            </Button>
            {error && (
              <Alert className="w-full" role="alert" variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </form>
        )}

        {clients.length === 0 ? (
          <AdminEmpty>Aucun client ne correspond à cette recherche.</AdminEmpty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5">Client</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="max-sm:hidden">Plan</TableHead>
                <TableHead className="text-right max-sm:pr-4">MRR</TableHead>
                <TableHead className="text-right max-sm:hidden">Utilisateurs</TableHead>
                <TableHead className="text-right max-sm:hidden">Contacts</TableHead>
                <TableHead className="pr-5 max-sm:hidden">Créé le</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map((client) => (
                <TableRow key={client.id}>
                  <TableCell className="pl-5">
                    <Link className="font-semibold hover:text-foreground" href={`/admin/clients/${client.id}`}>
                      {client.name}
                    </Link>
                    {client.website && (
                      <small className="block text-[12px] text-muted-foreground">{client.website}</small>
                    )}
                  </TableCell>
                  <TableCell>
                    <AdminChip tone={client.status}>
                      {CLIENT_STATUS_LABELS[client.status] ?? client.status}
                    </AdminChip>
                  </TableCell>
                  <TableCell className="max-sm:hidden">{client.subscription?.plan_id ?? "—"}</TableCell>
                  <TableCell className="text-right tabular-nums max-sm:pr-4">
                    {formatCents(client.subscription?.status === "active" ? client.subscription.mrr_cents : 0)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums max-sm:hidden">{client.member_count}</TableCell>
                  <TableCell className="text-right tabular-nums max-sm:hidden">{client.contact_count}</TableCell>
                  <TableCell className="pr-5 whitespace-nowrap text-muted-foreground tabular-nums max-sm:hidden">
                    {formatDate(client.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </AdminPanel>
    </>
  );
}
